import { defineStore, acceptHMRUpdate } from 'pinia'
import { formatTimeAgo } from '@R/utils/formatTimeAgo.ts'

export const useDashDataStore = defineStore(
    'dashData',
    () => {
        // State
        const actions = ref<any[]>([])
        const invoices = ref<any[]>([])
        const servers = ref<any[]>([])
        const tickets = ref<any[]>([])
        const intervalId = ref<ReturnType<typeof setInterval> | null>(null)

        // Actions
        async function fetchDashData(authToken: string): Promise<void> {
            if (!authToken) {
                console.warn('JWT is not set, skipping dashboard fetch.')
                return
            }

            try {
                if (import.meta.client) {
                    const response = await fetch('/api/v1/dashboard', {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${authToken}` },
                    })

                    if (!response.ok) throw new Error('Failed to fetch dashboard data')
                    const data = await response.json()

                    actions.value = data.actions ?? []
                    tickets.value = data.tickets ?? []
                    servers.value = data.servers ?? []
                    invoices.value = data.invoices ?? []

                    formatTimeAgo(actions.value)
                    formatTimeAgo(tickets.value)

                    startTimeAgoUpdater()
                }
            } catch (err) {
                console.error('Error fetching dash data:', err)
            }
        }

        function startTimeAgoUpdater(): void {
            if (intervalId.value) clearInterval(intervalId.value)
            intervalId.value = setInterval(() => {
                formatTimeAgo(actions.value)
                formatTimeAgo(tickets.value)
                // add more tables here if needed
            }, 5000)
        }

        function stopTimeAgoUpdater(): void {
            if (intervalId.value) {
                clearInterval(intervalId.value)
                intervalId.value = null
            }
        }

        function resetDashData(): void {
            stopTimeAgoUpdater()
            actions.value = []
            tickets.value = []
            servers.value = []
            invoices.value = []
        }

        return {
            // state
            actions,
            invoices,
            servers,
            tickets,
            intervalId,
            // actions
            fetchDashData,
            startTimeAgoUpdater,
            stopTimeAgoUpdater,
            resetDashData,
        }
    },
    { persist: true }
)

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useDashDataStore, import.meta.hot))
}
